/**
 * Auth API - Authentication API calls
 */

import { apiClient } from '../../../shared/api';
import type {
  LoginTarget,
  LoginRequest,
  RegisterRequest,
  RegisterVerifyRequest,
  RegisterVerifyResponse,
  AuthResponse,
  RegisterResponse,
  IdentifyRequest,
  IdentifyResponse,
  AuthenticateRequest,
  AuthUser,
  VerifyIdentityRequest,
  VerifyIdentityResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  ChangePasswordRequest,
  ChangePasswordResponse,
  IdentifyByNameRequest,
  IdentifyByNameResponse,
  SendOtpRequest,
  SendOtpResponse,
  VerifyOtpRequest,
  IdentifyByEmailRequest,
  RevokeAllSessionsResponse,
} from '@aegislab/shared';

/**
 * Multi-step Login Step 1: Identify user by username
 * Returns whether user exists and masked legal name if exists
 */
export const identify = async (data: IdentifyRequest): Promise<IdentifyResponse> => {
  const response = await apiClient.post<IdentifyResponse>('/auth/login/identify', data);
  return response.data;
};

/**
 * Multi-step Login Step 2: Authenticate with password
 * Returns auth response with tokens if valid
 */
export const authenticate = async (data: AuthenticateRequest): Promise<AuthResponse> => {
  const response = await apiClient.post<AuthResponse>('/auth/login/authenticate', data);
  return response.data;
};

/**
 * Login with credentials (single-step)
 */
export const login = async (data: LoginRequest): Promise<AuthResponse> => {
  const response = await apiClient.post<AuthResponse>('/auth/login', data);
  return response.data;
};

/**
 * 가입 요청 — 주소만 보내면 그 주소로 인증코드가 간다(87bac89b). 다시 보내기도 이 요청이다(60초 안에 보낸
 * 코드가 살아 있으면 서버가 다시 보내지 않는다). 응답에 계정·토큰이 없고, 확인(registerVerify)이 끝나야
 * 가입이 끝난다.
 */
export const register = async (data: RegisterRequest): Promise<RegisterResponse> => {
  const response = await apiClient.post<RegisterResponse>('/auth/register', data);
  return response.data;
};

/** 가입 확인 — 메일로 받은 6자리 인증코드와 함께 이름·비밀번호를 보낸다 */
export const registerVerify = async (
  data: RegisterVerifyRequest
): Promise<RegisterVerifyResponse> => {
  const response = await apiClient.post<RegisterVerifyResponse>('/auth/register/verify', data);
  return response.data;
};

/**
 * Logout - Revoke all refresh tokens on the server
 * Requires valid access token in Authorization header
 */
export const logout = async (): Promise<void> => {
  await apiClient.post('/auth/logout');
};

/**
 * Password Reset Step 1: Verify identity by email + username (실명)
 */
export const verifyIdentityForReset = async (
  data: VerifyIdentityRequest
): Promise<VerifyIdentityResponse> => {
  const response = await apiClient.post<VerifyIdentityResponse>(
    '/auth/password/verify-identity',
    data
  );
  return response.data;
};

/**
 * Password Reset Step 2: Reset password with token
 */
export const resetPassword = async (data: ResetPasswordRequest): Promise<ResetPasswordResponse> => {
  const response = await apiClient.post<ResetPasswordResponse>('/auth/password/reset', data);
  return response.data;
};

/**
 * Change the authenticated user's password.
 * Revokes all other active sessions while keeping the current session.
 */
export const changeOwnPassword = async (
  data: ChangePasswordRequest
): Promise<ChangePasswordResponse> => {
  const response = await apiClient.patch<ChangePasswordResponse>('/auth/password', data);
  return response.data;
};

/**
 * Passkey: Get registration options
 */
export const getPasskeyRegistrationOptions = async (crossPlatform?: boolean): Promise<unknown> => {
  const response = await apiClient.post<unknown>('/auth/passkey/register/options', {
    crossPlatform,
  });
  return response.data;
};

/**
 * Passkey: Verify registration
 */
export const verifyPasskeyRegistration = async (
  body: Record<string, unknown>,
  friendlyName?: string
): Promise<unknown> => {
  const response = await apiClient.post<unknown>('/auth/passkey/register/verify', {
    ...body,
    friendlyName,
  });
  return response.data;
};

/**
 * Passkey: List user's passkeys
 */
export const listPasskeys = async (): Promise<unknown> => {
  const response = await apiClient.get<unknown>('/auth/passkey');
  return response.data;
};

/**
 * Passkey: Delete a passkey
 */
export const deletePasskey = async (passkeyId: string) => {
  await apiClient.delete(`/auth/passkey/${passkeyId}`);
};

/**
 * Passkey: Rename a passkey
 */
export const renamePasskey = async (passkeyId: string, friendlyName: string) => {
  await apiClient.patch(`/auth/passkey/${passkeyId}`, { friendlyName });
};

/**
 * Passkey Login: Get authentication options
 */
export const getPasskeyLoginOptions = async (target: LoginTarget): Promise<unknown> => {
  const response = await apiClient.post<unknown>('/auth/passkey/login/options', target);
  return response.data;
};

/**
 * Passkey Login: Verify authentication
 */
export const verifyPasskeyLogin = async (
  target: LoginTarget,
  credential: Record<string, unknown>
): Promise<unknown> => {
  const response = await apiClient.post<unknown>('/auth/passkey/login/verify', {
    ...target,
    credential,
  });
  return response.data;
};

/**
 * Name-based Login: Identify user by legal name
 */
export const identifyByName = async (
  data: IdentifyByNameRequest
): Promise<IdentifyByNameResponse> => {
  const response = await apiClient.post<IdentifyByNameResponse>(
    '/auth/login/identify-by-name',
    data
  );
  return response.data;
};

/**
 * Name-based Login: Identify user by legal name + email (disambiguation)
 */
export const identifyByEmail = async (
  data: IdentifyByEmailRequest
): Promise<IdentifyByNameResponse> => {
  const response = await apiClient.post<IdentifyByNameResponse>(
    '/auth/login/identify-by-name',
    data
  );
  return response.data;
};

/**
 * OTP: Send one-time password to user's email
 */
export const sendOtp = async (data: SendOtpRequest): Promise<SendOtpResponse> => {
  const response = await apiClient.post<SendOtpResponse>('/auth/login/send-otp', data);
  return response.data;
};

/**
 * OTP: Verify the one-time password and authenticate
 */
export const verifyOtp = async (data: VerifyOtpRequest): Promise<AuthResponse> => {
  const response = await apiClient.post<AuthResponse>('/auth/login/verify-otp', data);
  return response.data;
};

async function fetchMe(): Promise<AuthUser> {
  const response = await apiClient.get<AuthUser>('/users/me');
  return response.data;
}

/**
 * Admin: Revoke all active refresh tokens for a user (force-logout all devices).
 * Reason is required (min 10 chars) and stored in AuditLog automatically.
 */
export const revokeAllUserSessions = async (
  userId: string,
  reason: string
): Promise<RevokeAllSessionsResponse> => {
  const response = await apiClient.post<RevokeAllSessionsResponse>(
    `/auth/admin/users/${userId}/revoke-all`,
    { reason }
  );
  return response.data;
};

export const authApi = {
  identify,
  authenticate,
  login,
  register,
  registerVerify,
  logout,
  verifyIdentityForReset,
  resetPassword,
  changeOwnPassword,
  getPasskeyRegistrationOptions,
  verifyPasskeyRegistration,
  listPasskeys,
  deletePasskey,
  renamePasskey,
  getPasskeyLoginOptions,
  verifyPasskeyLogin,
  identifyByName,
  identifyByEmail,
  sendOtp,
  verifyOtp,
  fetchMe,
  revokeAllUserSessions,
};
